
import {useEffect, useReducer} from 'react';
import {omit} from 'lodash';

import {ApptResult, useAppt} from './query';

export enum Step {
	Initializing,

	CheckAppt,
	Pricing,
	Expired,
	Error,

	CheckExit,
	Exiting,
}

type Reduced = {
	step: Step
	last?: Step
	error?: string
}

export type State = Reduced & {
	result: ApptResult
}

export type Action =
  | { type: 'EXIT-REQUEST' | 'EXIT-CONFIRM' | 'EXIT-CANCEL' }
  | { type: 'INITIALIZED' }
  | { type: 'APPT-CHECKED' }
  | { type: 'EXPIRED' }
  | { type: 'ERROR', error: string };

const reducer = (state: Reduced, action: Action): Reduced => {
	// Group handling of exit actions together
	// as they're tied to the hold logic as well
	switch (action.type) {
	case 'EXIT-REQUEST':
		// For a few known states, enable direct exit
		if (
			state.step === Step.Expired ||
			state.step === Step.Error ||
			state.step === Step.CheckExit
		) {
			return {
				...omit(state, 'last'),
				step: Step.Exiting,
			};
		}
		// Otherwise, verify before releasing appt hold
		return {
			...state,
			last: state.step,
			step: Step.CheckExit,
		};

	case 'EXIT-CONFIRM':
		return {
			...omit(state, 'last'),
			step: Step.Exiting,
		};

	case 'EXIT-CANCEL':
		return {
			...omit(state, 'last'),
			step: state.last || Step.Initializing,
		};

	case 'EXPIRED':
		return {
			...state,
			step: Step.Expired,
		};

	case 'ERROR':
		return {
			...state,
			step: Step.Error,
			error: action.error,
		};
	}

	// Here, handle transitions between logged in,
	// appt confirmation, package selection, etc
	switch (action.type) {
	case 'INITIALIZED':
		return {
			...state,
			step: Step.CheckAppt,
		};

	case 'APPT-CHECKED':
		return {
			...state,
			step: Step.Pricing,
		};
	}
};

export const useState = (appt: string, onHide: () => void) => {
	const result = useAppt(appt);
	const [state, dispatch] = useReducer(reducer, {
		step: Step.Initializing,
	});

	// Check for and execute state-dependent side effects
	useEffect(() => {

		// Check if we're already authenticated
		if (state.step === Step.Initializing) {
			if (result.data) {
				dispatch({type: 'INITIALIZED'});
			}
		}

		// If confirmed exit, call onHide
		else if (state.step === Step.Exiting) {
			onHide();
		}
	}, [
		state.step,
		result.data,
	]);

	return {
		state: {
			...state,
			result,
		},
		dispatch,
	};
};
